// src/components/PhotoUploader.jsx
// Subidor del panel: comprime y sube varias a la vez, muestra el avance por foto
// y permite reintentar las que fallen. Guardar en la base de datos lo decide quien lo usa (onSubidas).
import { useEffect, useState } from 'react';
import { uploadToCloudinary, cld } from '../services/cloudinaryService';
import '../styles/photoUploader.css';

const A_LA_VEZ = 3;
let siguienteId = 0;
const ETIQUETA = { espera: 'En cola', subiendo: 'Subiendo…', lista: 'Lista', error: 'Falló' };
const esImagen = (f) => f.type.startsWith('image/') || /\.hei[cf]$/i.test(f.name);

export default function PhotoUploader({ folder, onSubidas, multiple = true, conDescripcion = false }) {
    const [items, setItems] = useState([]); // { id, file, estado, url?, error? }
    const [descripcion, setDescripcion] = useState('');
    const [aviso, setAviso] = useState(null); // { tipo: 'ok' | 'error', texto }
    const [arrastrando, setArrastrando] = useState(false);

    const subiendo = items.some((i) => i.estado === 'espera' || i.estado === 'subiendo');
    const hechas = items.filter((i) => i.estado === 'lista' || i.estado === 'error').length;
    const fallidas = items.filter((i) => i.estado === 'error');

    // Evita cerrar la pestaña a media subida
    useEffect(() => {
        if (!subiendo) return;
        const avisar = (e) => e.preventDefault();
        window.addEventListener('beforeunload', avisar);
        return () => window.removeEventListener('beforeunload', avisar);
    }, [subiendo]);

    const marcar = (id, cambios) => setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...cambios } : i)));

    const subir = async (lista) => {
        setAviso(null);
        lista.forEach((it) => marcar(it.id, { estado: 'espera', error: undefined }));
        const resultados = new Array(lista.length); // conserva el orden en que se eligieron
        let siguiente = 0;
        const trabajador = async () => {
            while (siguiente < lista.length) {
                const n = siguiente++;
                const it = lista[n];
                marcar(it.id, { estado: 'subiendo' });
                const r = await uploadToCloudinary(it.file, folder);
                if (r.success) {
                    resultados[n] = { url: r.url, publicId: r.publicId, nombre: it.file.name };
                    marcar(it.id, { estado: 'lista', url: r.url });
                } else {
                    marcar(it.id, { estado: 'error', error: r.error });
                }
            }
        };
        await Promise.all(Array.from({ length: Math.min(A_LA_VEZ, lista.length) }, trabajador));

        const ok = resultados.filter(Boolean);
        if (ok.length) {
            try {
                await onSubidas(ok, descripcion);
                setDescripcion('');
            } catch (e) {
                console.error(e);
                setAviso({ tipo: 'error', texto: 'Las fotos se subieron pero no se pudieron guardar. Intenta de nuevo.' });
                return;
            }
        }
        const malas = lista.length - ok.length;
        setAviso(malas
            ? { tipo: 'error', texto: `${ok.length} subida(s), ${malas} fallaron.` }
            : { tipo: 'ok', texto: `Listo: ${ok.length} foto(s) subida(s).` });
    };

    const elegir = (files) => {
        const nuevos = [...files].filter(esImagen).slice(0, multiple ? undefined : 1)
            .map((file) => ({ id: siguienteId++, file, estado: 'espera' }));
        if (!nuevos.length) return;
        setItems(nuevos);
        subir(nuevos);
    };

    const soltar = (e) => {
        e.preventDefault();
        setArrastrando(false);
        if (!subiendo) elegir(e.dataTransfer.files);
    };

    return (
        <div
            className={`photo-uploader ${arrastrando ? 'arrastrando' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
            onDragLeave={() => setArrastrando(false)}
            onDrop={soltar}
        >
            <div className="uploader-content">
                {conDescripcion && (
                    <div className="description-input">
                        <textarea
                            aria-label="Descripción"
                            placeholder="Descripción para estas fotos (opcional)"
                            value={descripcion}
                            onChange={(e) => setDescripcion(e.target.value)}
                            rows="3"
                            disabled={subiendo}
                        />
                    </div>
                )}

                <label className={`upload-btn ${subiendo ? 'disabled' : ''}`}>
                    <input
                        type="file"
                        accept="image/*"
                        multiple={multiple}
                        disabled={subiendo}
                        onChange={(e) => { elegir(e.target.files); e.target.value = ''; }}
                    />
                    {subiendo ? 'Subiendo…' : multiple ? 'Elegir fotos' : 'Elegir foto'}
                </label>
                <p className="upload-hint">
                    {multiple ? 'O arrástralas aquí.' : 'O arrástrala aquí.'} Se comprimen solas antes de subir.
                </p>

                {items.length > 0 && (
                    <div className="subida-estado">
                        <progress value={hechas} max={items.length} aria-label="Avance de la subida" />
                        <p role="status" className={`subida-aviso ${aviso?.tipo || ''}`}>
                            {subiendo ? `Subiendo ${hechas} de ${items.length}…` : aviso?.texto}
                        </p>
                        {fallidas.length > 0 && !subiendo && (
                            <button type="button" className="subida-reintentar" onClick={() => subir(fallidas)}>
                                Reintentar {fallidas.length === 1 ? 'la que falló' : `las ${fallidas.length} que fallaron`}
                            </button>
                        )}
                        <ul className="subida-lista">
                            {items.map((i) => (
                                <li key={i.id} className={`subida-item ${i.estado}`} title={i.error || i.file.name}>
                                    {i.url
                                        ? <img src={cld(i.url, 200)} alt="" />
                                        : <span className="subida-nombre">{i.file.name}</span>}
                                    <span className="subida-badge">{ETIQUETA[i.estado]}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
}
